import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../contexts/AppContext";
import { toast } from "sonner";
import { LogIn, Award, Trophy, Clock, Download } from "lucide-react";
import RichText from "../components/RichText";
import { COUNTRIES, DEFAULT_COUNTRY } from "../lib/countries";
import Flag from "../components/Flag";
import TermsAcceptance from "../components/TermsAcceptance";
import SiteButton from "../components/SiteButton";

const fmt = (s) => `${(s || 0).toFixed(1)}s`;

export default function Compete() {
  const navigate = useNavigate();
  const { t, lang } = useApp();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [quizId, setQuizId] = useState("");
  const [quizName, setQuizName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [school, setSchool] = useState("");
  const [nationality, setNationality] = useState(DEFAULT_COUNTRY);
  const [accepted, setAccepted] = useState(false);
  const [step, setStep] = useState(1); // 1 = login, 2 = name+school, 3 = already-used result
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const tr = (pt, en) => (lang !== "en" ? pt : en);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/play/compete/login", { username, password, lang });
      setQuizId(data.quiz_id);
      setQuizName(lang === "en" ? data.quiz_name_en : (data.quiz_name_br || data.quiz_name_pt));
      if (data.already_used && data.result) {
        setResult(data.result);
        setStep(3);
      } else {
        setStep(2);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    } finally {
      setBusy(false);
    }
  };

  const doStart = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error(tr("Tem de aceitar os Termos e a Política de Privacidade.", "You must accept the Terms and Privacy Policy."));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/play/compete/start", { quiz_id: quizId, username, password, name, school, nationality });
      sessionStorage.setItem("participant_id", data.participant_id);
      sessionStorage.setItem("participant_name", name);
      navigate(`/play/competition/${quizId}`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        // Already used between login and start — re-fetch the result view
        try {
          const { data } = await api.post("/play/compete/login", { username, password, lang });
          if (data.result) { setResult(data.result); setStep(3); }
        } catch {}
        toast.error(tr("Estas credenciais já foram utilizadas.", "These credentials have already been used."));
      } else {
        toast.error(err.response?.data?.detail || "Erro");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-6 md:px-10 pt-10 pb-20" data-testid="compete-page">
      <h1 className="font-fredoka text-4xl md:text-5xl font-bold text-[#0B1D3A] mb-8">
        <RichText k="compete_title" />
      </h1>

      <div className="rounded-3xl bg-white p-7 md:p-9 shadow-[0_12px_40px_-10px_rgba(11,29,58,0.15)] border border-[#0B1D3A]/5">
        {step === 1 && (
          <form onSubmit={doLogin} className="space-y-4" data-testid="compete-login-form">
            <div>
              <label className="block font-nunito font-bold text-sm text-[#0B1D3A] mb-2"><RichText k="compete_username" /></label>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-2xl border-2 border-[#0B1D3A]/10 focus:border-[#005F73] outline-none font-nunito"
                data-testid="compete-username-input"
              />
            </div>
            <div>
              <label className="block font-nunito font-bold text-sm text-[#0B1D3A] mb-2"><RichText k="compete_password" /></label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-2xl border-2 border-[#0B1D3A]/10 focus:border-[#005F73] outline-none font-nunito"
                data-testid="compete-password-input"
              />
            </div>
            <SiteButton type="submit" disabled={busy} color="#005F73" icon={LogIn} testId="compete-login-btn">
              <RichText k="compete_login" />
            </SiteButton>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={doStart} className="space-y-4" data-testid="compete-info-form">
            {quizName && (
              <p className="font-nunito text-sm text-[#0B1D3A]/70">
                {lang !== "en" ? "Concurso" : "Contest"}: <span className="font-bold text-[#FF6B6B]">{quizName}</span>
              </p>
            )}
            <div>
              <label className="block font-nunito font-bold text-sm text-[#0B1D3A] mb-2"><RichText k="compete_name" /></label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-2xl border-2 border-[#0B1D3A]/10 focus:border-[#005F73] outline-none font-nunito"
                data-testid="compete-name-input"
              />
            </div>
            <div>
              <label className="block font-nunito font-bold text-sm text-[#0B1D3A] mb-2"><RichText k="compete_school" /></label>
              <input
                value={school}
                onChange={(e) => setSchool(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-2xl border-2 border-[#0B1D3A]/10 focus:border-[#005F73] outline-none font-nunito"
                data-testid="compete-school-input"
              />
            </div>
            <div>
              <label className="block font-nunito font-bold text-sm text-[#0B1D3A] mb-2">{tr("Nacionalidade", "Nationality")}</label>
              <select
                value={nationality}
                onChange={(e) => setNationality(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-2xl border-2 border-[#0B1D3A]/10 focus:border-[#005F73] outline-none font-nunito bg-white"
                data-testid="compete-nationality-select"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{lang !== "en" ? c.pt : c.en}</option>
                ))}
              </select>
            </div>
            <div className="pt-1">
              <TermsAcceptance checked={accepted} onChange={setAccepted} lang={lang} testId="compete-terms" />
            </div>
            <SiteButton type="submit" disabled={busy} color="#1B5E3A" testId="compete-start-btn">
              <RichText k="compete_start" />
            </SiteButton>
          </form>
        )}

        {step === 3 && result && (
          <div className="text-center" data-testid="compete-result-view">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#FFD93D] mb-4">
              <Trophy size={30} className="text-[#0B1D3A]" />
            </span>
            <h2 className="font-fredoka text-2xl font-bold text-[#0B1D3A]" data-testid="compete-result-name">{result.name}</h2>
            <p className="font-nunito text-[#0B1D3A]/70 mb-1 flex items-center justify-center gap-2" data-testid="compete-result-school">
              {result.school}
              {result.nationality && <Flag code={result.nationality} lang={lang} />}
            </p>
            <p className="font-nunito text-sm text-[#0B1D3A]/50 mb-6">
              {result.abandoned
                ? tr("Desististe deste concurso. Estas credenciais já não podem ser utilizadas.", "You withdrew from this contest. These credentials can no longer be used.")
                : tr("Estas credenciais já foram utilizadas. Aqui está o teu resultado:", "These credentials have already been used. Here is your result:")}
            </p>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="rounded-2xl bg-[#F8FAFC] p-4 border border-[#0B1D3A]/5">
                <p className="font-nunito text-xs uppercase font-bold text-[#0B1D3A]/50 flex items-center justify-center gap-1"><Clock size={13}/> {tr("Tempo final", "Final time")}</p>
                <p className="font-fredoka text-2xl font-bold text-[#005F73] mt-1" data-testid="compete-result-time">
                  {result.abandoned ? <span className="text-[#E63946]">{tr("Desistência", "Withdrawal")}</span> : (result.timed_out ? tr("Tempo esgotado", "Time ran out") : fmt(result.final_seconds))}
                </p>
              </div>
              <div className="rounded-2xl bg-[#F8FAFC] p-4 border border-[#0B1D3A]/5">
                <p className="font-nunito text-xs uppercase font-bold text-[#0B1D3A]/50 flex items-center justify-center gap-1"><Award size={13}/> {tr("Classificação", "Ranking")}</p>
                <p className="font-fredoka text-2xl font-bold text-[#FF6B6B] mt-1" data-testid="compete-result-ranking">
                  {result.abandoned ? "—" : <>{result.ranking}{result.ranking_total ? <span className="text-base text-[#0B1D3A]/50"> / {result.ranking_total}</span> : null}</>}
                </p>
              </div>
            </div>

            {result.abandoned ? (
              <div className="rounded-2xl bg-[#FDECEC] border border-[#E63946]/30 p-4 text-[#9B1C1C] font-nunito font-semibold" data-testid="compete-result-abandoned">
                {tr("Desistência registada. Não é possível voltar a jogar.", "Withdrawal recorded. You cannot play again.")}
              </div>
            ) : result.diplomas_available ? (
              <SiteButton
                href={`${backendUrl}/api/diploma/${result.participation_id}?lang=${lang}`}
                target="_blank"
                rel="noreferrer"
                color="#FF6B6B"
                icon={Download}
                testId="compete-result-diploma-btn"
              >
                {tr("Descarregar diploma", "Download diploma")}
              </SiteButton>
            ) : (
              <div className="rounded-2xl bg-[#FFF6E5] border border-[#FFD93D] p-4 text-[#856404] font-nunito font-semibold" data-testid="compete-result-unavailable">
                {result.diploma_unavailable_message || tr("Diploma indisponível. Volta mais tarde!", "Diploma unavailable. Come back later!")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
